import React, {
  useState,
  ChangeEvent,
  useRef,
  useEffect,
  useMemo,
} from "react";
import {
  FormTab,
  TextInput,
  SelectInput,
  BooleanInput,
  Record,
  regex,
  required,
  FormDataConsumer,
  useTranslate,
  maxLength,
  TabbedForm,
} from "react-admin";
import "../../common/styles/form-style.css";
import { useAppData } from "../../../common/hooks/useAppData";
import { DocumentUploader } from "../../common/form/DocumentUploader";
import { LicenceStateSelector } from "../../common/form/LicenceStateSelector";
import { NameAndSurnameInput } from "./NameAndSurnameInput";
import { CycleRulesSelector } from "../../common/form/CycleRulesSelector";
import Inputmask from "inputmask";
import { useDefaultHosData } from "../../../common/hooks/useDefaultHosData";
import { DriverDefaultValuesHandler } from "./DriverDefaultValuesHandler";
import { ICompanyDefaultHosData } from "../../../common/redux/actions/defaultHosData";
import { DriverListDto, DriverDto } from "../../../common/data/service-proxies";
import { GenericDateOrTimePicker } from "../../common/form/GenericDateOrTimePicker";
import { DatePicker } from "@material-ui/pickers";
import { IdAndEmailInput } from "./IdAndEmailInput";
import { FormToolbar } from "../../common/form/FormToolbar";
import { ProtectedElement } from "../../common/field-permissions/ProtectedElement";

interface DriverFormProps {
  isEdit?: boolean;
  record?: DriverListDto & DriverDto & Record;
}

export const DriverForm = ({ isEdit, ...rest }: DriverFormProps) => {
  const { record } = rest;
  const defaultHosDataState = useDefaultHosData();
  const [currentDefaultHosData, setCurrentDefaultHosData] = useState<
    Omit<ICompanyDefaultHosData, "companyId">
  >();

  const translate = useTranslate();
  const countries = useAppData((x) => x.countries);
  const companies = useAppData((x) => x.companies);
  const cargoTypeValues = useAppData((x) => x.cargoTypes) ?? [];
  const cargoTypes = cargoTypeValues.map((x) => ({
    id: x,
    name: translate(`appData.cargoTypes.${x}`),
  }));
  const driverTypeValues = useAppData((x) => x.driverTypes) ?? [];
  const driverTypes = driverTypeValues.map((x) => ({
    id: x,
    name: translate(`appData.driverTypes.${x}`),
  }));
  const documentTypes =
    useAppData((x) =>
      x.knownDocumentTypes ? x.knownDocumentTypes["Driver"] : []
    ) ?? [];

  const phoneRef = useRef<HTMLInputElement>(null);
  const groupsRef = useRef<Array<string>>();

  useEffect(() => {
    if (!phoneRef.current) {
      return;
    }

    var im = new Inputmask("(999) 999-9999");
    im.mask(phoneRef.current);
  });

  useEffect(() => {
    // To fix the difference between list and form models
    if (!record) {
      return;
    }

    if (record.fullName && !record.driverName) {
      record.driverName = record.fullName;
    }

    if (record.cycleRules) {
      record.drivingCycles = record.cycleRules.map(
        (x) =>
          countries
            .find((c) => c.id === x.drivingCountryId)
            ?.dutyCycles?.find((d) => d.id === x.cycleRuleId)?.name!
      );
    }

    if (record.groups) {
      groupsRef.current = [...record.groups];
    } else if (groupsRef.current) {
      record.groups = [...groupsRef.current];
    }
  }, [record, countries]);

  const defaultCompany = useMemo(
    () => (companies.length === 1 ? companies[0] : undefined),
    [companies]
  );

  useEffect(() => {
    if (!defaultCompany) {
      return;
    }

    setCurrentDefaultHosData(defaultHosDataState[defaultCompany.id!]);
  }, [defaultCompany, defaultHosDataState]);

  return (
    <TabbedForm
      className="form-create-edit"
      variant="outlined"
      redirect="list"
      {...rest}
      toolbar={<FormToolbar showDeleteButton={false} />}
    >
      <FormTab label="resources.admin-drivers.labels.personalInformation">
        <NameAndSurnameInput />
        <IdAndEmailInput edit={isEdit} />
        <ProtectedElement source="dateOfBirth">
          <GenericDateOrTimePicker
            PickerComponent={DatePicker}
            source="dateOfBirth"
            label="resources.admin-drivers.labels.dateOfBirth"
            options={{
              autoOk: true,
              format: "MMMM do yyyy",
            }}
          />
        </ProtectedElement>
        <ProtectedElement source="address">
          <TextInput
            source="address"
            label="resources.admin-drivers.labels.address"
          />
        </ProtectedElement>
        <ProtectedElement source="driverType">
          <SelectInput
            source="driverType"
            choices={driverTypes}
            validate={required()}
            label="resources.admin-drivers.labels.driverType"
          />
        </ProtectedElement>
        <ProtectedElement source="phoneNumber">
          <TextInput
            source="phoneNumber"
            label="resources.common.labels.phone"
            validate={[
              required(),
              regex(
                /^(\([0-9]{3}\) |[0-9]{3}-)[0-9]{3}-[0-9]{4}$/,
                translate("resources.common.labels.mustBeValidPhoneNumber")
              ),
            ]}
            inputRef={phoneRef}
          />
        </ProtectedElement>
        <FormDataConsumer>
          {(formDataProps) => (
            <LicenceStateSelector
              countryIdProp="licenceCountryId"
              stateIdProp="licenceStateId"
              countries={countries}
              {...formDataProps}
            />
          )}
        </FormDataConsumer>
        <ProtectedElement source="licenceNumber">
          <TextInput
            source="licenceNumber"
            label="resources.admin-drivers.labels.licenceNumber"
            validate={[required(), maxLength(20)]}
          />
        </ProtectedElement>
      </FormTab>
      <FormTab label="resources.admin-drivers.labels.hosEldOptions">
        <ProtectedElement source="companyId">
          <SelectInput
            source="companyId"
            label="resources.admin-drivers.labels.companyName"
            choices={companies}
            validate={required()}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const currentDefault =
                defaultHosDataState[parseInt(event.target.value)];
              setCurrentDefaultHosData(currentDefault);
            }}
            defaultValue={defaultCompany?.id}
          />
        </ProtectedElement>
        <CycleRulesSelector
          source="cycleRules"
          validate={required()}
          countries={countries}
          label="resources.admin-drivers.labels.cycleRules"
        />
        <ProtectedElement source="cargoType">
          <SelectInput
            source="cargoType"
            label="resources.admin-drivers.labels.cargoType"
            choices={cargoTypes}
            validate={required()}
          />
        </ProtectedElement>
        {/* <ProtectedElement source="brakeException30Minutes">
          <BooleanInput
            source="brakeException30Minutes"
            label="resources.admin-drivers.labels.breakException30mins"
          />
        </ProtectedElement> */}
        {/* <ProtectedElement source="cycleReset24Hours">
          <BooleanInput
            source="cycleReset24Hours"
            label="resources.admin-drivers.labels.cycleReset24hours"
          />
        </ProtectedElement> */}
        <ProtectedElement source="exemptFromEld">
          <BooleanInput
            source="exemptFromEld"
            label="resources.admin-drivers.labels.exemptFromEld"
          />
        </ProtectedElement>
        <ProtectedElement source="allowLogEditing">
          <BooleanInput source="allowLogEditing" label="Can edit logs" />
        </ProtectedElement>
        <ProtectedElement source="allowYardMove">
          <BooleanInput
            source="allowYardMove"
            label="resources.admin-drivers.labels.allowYardMove"
          />
        </ProtectedElement>
        <ProtectedElement source="allowPersonalConveyance">
          <BooleanInput
            source="allowPersonalConveyance"
            label="resources.admin-drivers.labels.allowPersonalConveyance"
          />
        </ProtectedElement>
        <ProtectedElement source="allowManualDriveTime">
          <BooleanInput
            source="allowManualDriveTime"
            label="resources.admin-drivers.labels.allowManualDriveTime"
          />
        </ProtectedElement>
        <ProtectedElement source="hideUnidentifiedEvents">
          <BooleanInput
            source="hideUnidentifiedEvents"
            label="resources.admin-drivers.labels.hideUnidentifiedEvents"
          />
        </ProtectedElement>
      </FormTab>
      <FormTab label="resources.admin-drivers.labels.documents">
        <ProtectedElement source="documents">
          <DocumentUploader source="documents" documentTypes={documentTypes} />
        </ProtectedElement>
      </FormTab>
      <FormDataConsumer>
        {(props) => (
          <DriverDefaultValuesHandler
            {...props}
            {...currentDefaultHosData}
            isEdit={isEdit ?? false}
          />
        )}
      </FormDataConsumer>
    </TabbedForm>
  );
};
